import { yupValidations } from '@ianesthesie-kit/form';
import { Gender, Region } from '@ianesthesie-kit/services';
import * as Yup from 'yup';
import { matchIsValidTel } from '@ianesthesie-kit/tel-input';
import { InsertionType } from '@/modules/appContext';
import { appStore } from '@/store';
import { captureThrowOnDev } from '@/util';
import pick from 'lodash/fp/pick';

export const pendingPatientValidation = yupValidations.create({
  firstName: yupValidations.requiredText,
  lastName: yupValidations.requiredText,
  birthDate: yupValidations.pastDateString,
  gender: yupValidations.oneOfEnum(Gender),
  deadline: yupValidations.requiredFutureDateOrTodayString,
  phoneNumber: yupValidations.text,
  email: yupValidations.email,
  surgeonName: yupValidations.text,
  appointmentId: yupValidations.text,
  pendingPatientIdentifier: Yup.object({
    identifier: yupValidations.text.test({
      name: 'a',
      message: 'app.validation.required',
      test: (v, ctx) => (ctx.parent.type ? !!v : true)
    }),
    type: yupValidations.text.test({
      name: 'b',
      message: 'app.validation.required',
      test: (v, ctx) => (ctx.parent.identifier ? !!v : true)
    })
  }).default(undefined),
  epicExtPatientId: yupValidations.text.default(undefined)
});

export type PendingPatientSchema = Yup.InferType<
  typeof pendingPatientValidation
>;

export const directPatientValidation = yupValidations.create({
  firstName: yupValidations.requiredText,
  lastName: yupValidations.requiredText,
  middleName: yupValidations.text,
  maidenName: yupValidations.text,
  birthDate: yupValidations.requiredPastDateOrTodayString,
  gender: yupValidations.requiredOneOfEnum(Gender),
  phoneNumber: yupValidations.text,
  email: yupValidations.email,
  surgeryDate: yupValidations.text.when('urgentCareNeeded', {
    is: true,
    then: yupValidations.futureDateOrTodayString,
    otherwise: yupValidations.futureDateString
  }),
  pregnant: yupValidations.bool,
  epicExtPatientId: yupValidations.text.default(undefined),
  username: yupValidations.username,
  urgentCareNeeded: yupValidations.bool.default(false),
  addressDetails: yupValidations
    .create({
      address: yupValidations.requiredText,
      city: yupValidations.requiredText,
      zipCode: yupValidations.requiredText,
      region: yupValidations.requiredOneOfEnum(Region)
    })
    .optional()
    .nullable()
});

export type DirectPatientSchema = Yup.InferType<typeof directPatientValidation>;

export type CombinedPatientSchema = PendingPatientSchema & DirectPatientSchema;

export const isTelValidCandidate = (tel?: string) => !!tel && tel.length > 4;

export const isValidTel = (tel?: string) =>
  !isTelValidCandidate(tel) || matchIsValidTel(tel!);

export function getPatientInitialFormValues(
  insertionType: undefined,
  addressDetails?: boolean
): Record<string, never>;
export function getPatientInitialFormValues(
  insertionType: Extract<InsertionType, 'patient'>,
  addressDetails?: boolean
): DirectPatientSchema;
export function getPatientInitialFormValues(
  insertionType: Extract<InsertionType, 'pendingPatient'>,
  addressDetails?: boolean
): PendingPatientSchema;
export function getPatientInitialFormValues(
  insertionType: InsertionType | undefined,
  addressDetails?: boolean
) {
  if (!insertionType) {
    return {};
  }

  if (insertionType !== 'patient' && insertionType !== 'pendingPatient') {
    captureThrowOnDev(`Unknown patient insertion type: ${insertionType}`);
    return {};
  }

  if (insertionType === 'pendingPatient') {
    return {
      ...pendingPatientInitialValues,
      ...pick(
        Object.keys(pendingPatientInitialValues),
        appStore.getState().appContext.newPatientDrawer.formValues
      )
    };
  }

  return {
    ...directPatientInitialValues,
    ...pick(
      Object.keys(directPatientInitialValues),
      appStore.getState().appContext.newPatientDrawer.formValues
    ),
    addressDetails: addressDetails
      ? {
          address: '',
          city: '',
          zipCode: '',
          region: '' as Region
        }
      : null
  };
}

const pendingPatientInitialValues: PendingPatientSchema = {
  firstName: '',
  lastName: '',
  birthDate: '',
  gender: '',
  deadline: '',
  phoneNumber: '',
  email: '',
  appointmentId: '',
  surgeonName: '',
  pendingPatientIdentifier: {
    identifier: '',
    type: ''
  },
  epicExtPatientId: undefined
};

const directPatientInitialValues: DirectPatientSchema = {
  username: '',
  firstName: '',
  lastName: '',
  maidenName: '',
  middleName: '',
  birthDate: '',
  gender: '',
  phoneNumber: '',
  email: '',
  surgeryDate: '',
  pregnant: undefined,
  epicExtPatientId: undefined,
  addressDetails: null,
  urgentCareNeeded: false
};
